/**
 * Interval helpers that pause while the tab is hidden and fire immediately on visible.
 * Does not touch game loop / canvas — network polls only.
 */

export type VisibilityIntervalHandle = {
  stop: () => void;
  /** Force one tick now (e.g. after sending a chat message). */
  tickNow: () => void;
};

/**
 * Run `tick` every `ms` while the document is visible.
 * On hide → pause. On show → immediate tick + resume.
 */
export function createVisibilityInterval(
  tick: () => void,
  ms: number,
  opts?: { runOnStart?: boolean },
): VisibilityIntervalHandle {
  let timer: ReturnType<typeof setInterval> | null = null;

  const clearTimer = () => {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const startTimer = () => {
    if (timer != null || typeof document === "undefined") return;
    if (document.hidden) return;
    timer = setInterval(() => {
      if (!document.hidden) tick();
    }, ms);
  };

  const onVisibility = () => {
    if (typeof document === "undefined") return;
    if (document.hidden) {
      clearTimer();
      return;
    }
    tick();
    startTimer();
  };

  if (typeof document !== "undefined") {
    if (!document.hidden) {
      if (opts?.runOnStart) tick();
      startTimer();
    }
    document.addEventListener("visibilitychange", onVisibility);
  }

  return {
    stop: () => {
      clearTimer();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    },
    tickNow: () => {
      if (typeof document !== "undefined" && document.hidden) return;
      tick();
    },
  };
}

export function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.hidden;
}
